<script lang="ts">
  /**
   * The re-entry sheet: one editable line that begins as the task's exact
   * wording.
   *
   * AF1's rule is that an unfinished task is re-entered at the end of the list
   * *before* it is crossed off (AUTOFOCUS_SYSTEM.md:57). This is where the user
   * chooses the wording of what comes back — and it commits **one**
   * `ReenterTask`, never N `AppendTask`s.
   *
   * That distinction is not cosmetic. `ReenterTask` is what transitions the
   * parent to `Reentered` and what makes the fold set `parent_task_id` on the
   * children (`fold.ts:477`); N appends would leave the parent `ACTIVE` forever
   * and spawn orphans. A15 allocates every child in a single commit with one
   * `child_assigned` vector, which is also what lets the run cross a page seal
   * without splitting into separate operations.
   *
   * The sheet owns **only** the draft titles. It authors nothing: `onCommit`
   * hands the list up and the host calls `store.reenterTask`.
   *
   * Two rulings it encodes (D8, §9.4):
   *
   * * **A re-entry never rewrites the parent.** There is no control here that
   *   could. Wording changes live on the children, and a child whose title equals
   *   its parent's is the correct verbatim case rather than a degenerate one.
   * * **No cap on children.** `REPLICATION.md:184` says `1..=N` and states no cap.
   *
   * One surface, no mode row (user ruling 2026-10-05): the wording starts as
   * the task's exact title — committing untouched is the verbatim re-entry —
   * and editing it or adding lines rewords / breaks the task down. The list
   * grows itself: no `[ Add another ]`, no per-row `- Remove` — the last row
   * typed non-blank conjures the next blank one, and blank (or whitespace-only)
   * rows are simply skipped at commit, so there is nothing to remove.
   *
   * Commit is `aria-disabled`, not `disabled`, for the same reason `PageFooter`
   * is: a disabled control is skipped by screen readers entirely, which would
   * hide the rule rather than explain it.
   */
  import Icon from './Icon.svelte';
  import RotateCcw from 'lucide/dist/esm/icons/rotate-ccw.mjs';
  import X from 'lucide/dist/esm/icons/x.mjs';
  import type { Task } from '../types/generated';

  interface Props {
    task: Task;
    onCommit: (titles: string[]) => void | Promise<void>;
    onCancel: () => void;
  }

  let { task, onCommit, onCancel }: Props = $props();

  // Row 0 IS the re-entry: pre-filled with the task's exact title, so
  // committing untouched re-enters it verbatim. The trailing blank row is the
  // affordance to reword or break down into more lines.
  // svelte-ignore state_referenced_locally
  let lines = $state<string[]>([task.title, '']);

  /** Blank rows are not children: the engine refuses a childless re-entry. */
  const committedTitles = $derived(
    lines.map((t) => t.trim()).filter((t) => t.length > 0),
  );

  const canCommit = $derived(committedTitles.length > 0);

  function onChildInput(index: number, value: string) {
    let next = lines.map((t, i) => (i === index ? value : t));
    // The sheet grows itself: once every row is non-blank, a fresh blank one
    // appears. Whitespace is blank. Appending never remounts the existing rows
    // (keyed by index), so the caret stays where the user is typing.
    if (next.every((t) => t.trim() !== '')) next = [...next, ''];
    lines = next;
  }

  function commit() {
    // Guard rather than trust: a childless `ReenterTask` is `MALFORMED_ENVELOPE`
    // at the fold boundary, so the control is disabled instead of authoring an
    // op that is guaranteed to be refused.
    if (!canCommit) return;
    void onCommit(committedTitles);
  }

  function onkeydown(event: KeyboardEvent) {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    // Keep the container's window-level Escape from also reading this as a
    // step-back out of the takeover; the sheet is the more specific surface.
    event.stopPropagation();
    onCancel();
  }
</script>

<!-- svelte-ignore a11y_no_noninteractive_element_interactions -->
<div
  class="reentry-sheet"
  data-testid="reentry-sheet"
  role="dialog"
  aria-modal="true"
  aria-label="Re-enter task"
  tabindex="-1"
  {onkeydown}
>
  <p class="reentry-parent" data-testid="reentry-parent" title="The parent is never reworded">
    {task.title}
  </p>

  <ul class="reentry-children">
    {#each lines as title, index (index)}
      <li class="reentry-child">
        <label class="reentry-child-label" for="reentry-child-{index}">
          <span class="reentry-arrow" aria-hidden="true">↳</span>
          <span class="sr-only">Child {index + 1}</span>
        </label>
        <input
          id="reentry-child-{index}"
          data-testid="reentry-child-{index}"
          type="text"
          autocomplete="off"
          spellcheck="false"
          value={title}
          placeholder="Optional next step"
          oninput={(e) => onChildInput(index, e.currentTarget.value)}
        />
      </li>
    {/each}
  </ul>

  <p class="reentry-note">
    Children go to the Final page, and the original is marked re-entered. Its wording is
    left exactly as you wrote it.
  </p>

  <div class="reentry-actions">
    <button class="action" type="button" data-testid="reentry-cancel" onclick={onCancel}>
      <Icon node={X} />
      Cancel
    </button>
    <button
      class="action action--dismiss"
      type="button"
      data-testid="reentry-commit"
      aria-disabled={!canCommit}
      title={canCommit
        ? 'Re-enter this task at the end of the list.'
        : 'Type at least one child — an empty re-entry is refused by the engine.'}
      onclick={commit}
    >
      <Icon node={RotateCcw} />
      Re-enter
    </button>
  </div>
</div>

<style>
  .reentry-sheet {
    /* A half-sheet: it sits over the bottom of the stage rather than centring,
       so the page above stays readable while the choice is made. */
    position: absolute;
    left: 50%;
    bottom: 0;
    transform: translateX(-50%);
    /* Load-bearing. The banner path renders this sheet INSIDE the hub branch,
       where `.hub-host` is a full-screen `position: fixed; z-index: 20`
       overlay: at `z-index: auto` this sheet paints BELOW it, visible through
       its 12% backdrop but not hit-testable — every button and input in the
       reword slot silently went to the hub instead. Above the hub (20), below
       the undo snackbar (30). */
    z-index: 25;
    width: min(100%, 46em);
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 14px 16px 16px;
    border-top: 1px solid color-mix(in srgb, var(--ink) 16%, transparent);
    border-radius: 4px 4px 0 0;
    background: var(--paper);
    box-shadow: var(--shadow-card);
    font-family: var(--font-ui);
    font-size: 13px;
    color: var(--ink);
  }

  .reentry-parent {
    margin: 0;
    /* Struck, not editable: this line is what was written, and a re-entry does
       not get to change it. */
    text-decoration: line-through;
    color: var(--ink-muted);
  }

  .reentry-actions {
    display: flex;
    gap: 8px;
    justify-content: flex-end;
  }

  .reentry-children {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 6px;
    max-height: 40vh;
    overflow-y: auto;
  }

  .reentry-child {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .reentry-child-label {
    display: inline-flex;
    width: 2ch;
    color: var(--ink-muted);
  }

  .reentry-child input {
    flex: 1;
    /* 16px keeps iOS Safari from zooming the viewport on focus. */
    font-size: 16px;
    padding: 5px 8px;
    border: 1px solid color-mix(in srgb, var(--ink) 50%, transparent);
    border-radius: 3px;
    /* `var(--paper)`, not the `#fffef9` literal it replaced: identical in
       light, and the reword field flips with the sheet in dark. */
    background: var(--paper);
    color: var(--ink);
  }

  .reentry-child input:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 1px;
  }

  .reentry-note {
    margin: 0;
    font-size: 11px;
    color: var(--ink-muted);
  }

  /* Visible to a screen reader, invisible on the page. The `↳` glyph on its own
     would otherwise be announced as a stray character. */
  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip-path: inset(50%);
    white-space: nowrap;
    border: 0;
  }
</style>
